'use client';

import { $generateNodesFromDOM } from '@lexical/html';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $insertNodes, COMMAND_PRIORITY_HIGH, PASTE_COMMAND } from 'lexical';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

function isGcsUrl(src: string): boolean {
  return src.includes('storage.googleapis.com');
}

function isBase64DataUri(src: string): boolean {
  return src.startsWith('data:image/');
}

function base64ToFile(dataUri: string): File {
  const [header, base64] = dataUri.split(',');
  const mimeMatch = header.match(/data:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/png';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  const ext = mime.split('/')[1] || 'png';
  return new File([bytes], `pasted-image.${ext}`, { type: mime });
}

async function urlToFile(url: string): Promise<File> {
  const response = await fetch(url);
  const blob = await response.blob();
  const ext = blob.type.split('/')[1] || 'png';
  return new File([blob], `pasted-image.${ext}`, { type: blob.type });
}

const MAX_IMAGE_WIDTH = 500;

function getImageDimensions(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = reject;
    img.src = url;
  });
}

export function PasteImageUploadPlugin({
  onImageUpload,
}: {
  onImageUpload?: (file: File) => Promise<string>;
}): null {
  const [editor] = useLexicalComposerContext();
  const isProcessingRef = useRef(false);

  useEffect(() => {
    if (!onImageUpload) return;

    return editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent) => {
        if (isProcessingRef.current) return false;

        const html = event.clipboardData?.getData('text/html');
        if (!html || !html.includes('<img')) return false;

        // Parse the HTML and find images that need uploading
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const imgs = doc.querySelectorAll('img');

        const needsUpload: HTMLImageElement[] = [];
        for (const img of imgs) {
          const src = img.getAttribute('src') || '';
          if (src && !isGcsUrl(src)) {
            needsUpload.push(img);
          }
        }

        if (needsUpload.length === 0) return false;

        // Block the paste — we'll insert the content after processing
        event.preventDefault();
        isProcessingRef.current = true;

        processAndInsert(doc, needsUpload).finally(() => {
          isProcessingRef.current = false;
        });

        return true;
      },
      COMMAND_PRIORITY_HIGH
    );

    async function processAndInsert(doc: Document, imgs: HTMLImageElement[]) {
      const toastId = toast.loading('Uploading pasted images...');

      try {
        // Upload all images in parallel
        const results = await Promise.all(
          imgs.map(async (img) => {
            const src = img.getAttribute('src') || '';
            try {
              const file = isBase64DataUri(src) ? base64ToFile(src) : await urlToFile(src);
              const gcsUrl = await onImageUpload?.(file);
              if (!gcsUrl) throw new Error('Upload returned no URL');
              img.setAttribute('src', gcsUrl);

              // Get natural dimensions and scale down to max width
              try {
                const dims = await getImageDimensions(gcsUrl);
                if (dims.width > MAX_IMAGE_WIDTH) {
                  const scale = MAX_IMAGE_WIDTH / dims.width;
                  img.setAttribute('width', Math.round(dims.width * scale).toString());
                  img.setAttribute('height', Math.round(dims.height * scale).toString());
                } else {
                  img.setAttribute('width', dims.width.toString());
                  img.setAttribute('height', dims.height.toString());
                }
              } catch {
                // If we can't get dimensions, leave them unset
              }

              return { success: true as const };
            } catch {
              return { success: false as const, src };
            }
          })
        );

        const failures = results.filter((r) => !r.success);

        if (failures.length > 0 && failures.length === results.length) {
          toast.dismiss(toastId);
          toast.error(
            'Could not paste — images failed to upload. Save the images to your computer first, then paste the text and use the toolbar upload button to add images.',
            { duration: 8000 }
          );
          return;
        }

        if (failures.length > 0) {
          // Some images failed — remove them from the HTML
          for (const img of imgs) {
            const src = img.getAttribute('src') || '';
            if (!isGcsUrl(src)) {
              img.remove();
            }
          }
          toast.dismiss(toastId);
          toast.warning(
            'Some images could not be uploaded. Save those images to your computer and use the toolbar upload button to add them.',
            { duration: 8000 }
          );
        }

        // Insert the processed HTML into the editor
        toast.dismiss(toastId);
        editor.update(() => {
          const nodes = $generateNodesFromDOM(editor, doc);
          $insertNodes(nodes);
        });
      } catch (error) {
        console.error('[PasteImageUpload] Unexpected error:', error);
        toast.dismiss(toastId);
        toast.error('Failed to paste content. Please try again.', { duration: 5000 });
      }
    }
  }, [editor, onImageUpload]);

  return null;
}
